import { useMutation } from "@tanstack/react-query";
import { z } from "zod";
import { getCoreAxiosInstance } from "../../api/CoreAxiosInstance";
import {
  CompanyElementMappingSchema,
  MappingsSavePayloadSchema,
  type CompanyElementMapping,
  type MappingsSavePayload,
} from "../types";

/**
 * Save (create or update) element→column mappings for a company.
 *
 * PUT /api/v1/christmas/companies/{companyCode}/pay-components/mappings
 *
 * Accepts a save payload with mapping entries plus replaceScopes so skipped
 * columns clear previously saved mappings for scopes shown on the Map Columns
 * screen.
 *
 * **Kept unchanged by LADR-039 (#440 Step 11, W3a).** The pay-components
 * vertical is hybrid: Map Columns keeps saving mappings through this
 * Christmas-direct endpoint — only the post-resolution stamp-and-commit steps
 * move to workloads (W3b, see `usePatchPayComponentAmbiguity`; commit itself
 * is Step 12). See `.plans/ladr-039-contract-mapping.md`'s pay components
 * section.
 */
export function useSaveMappings(companyCode: string) {
  return useMutation<CompanyElementMapping[], Error, MappingsSavePayload>({
    mutationFn: async (payload) => {
      const body = MappingsSavePayloadSchema.parse(payload);
      const instance = await getCoreAxiosInstance();
      const { data } = await instance.put(
        `/api/v1/christmas/companies/${companyCode}/pay-components/mappings`,
        body,
      );
      return z.array(CompanyElementMappingSchema).parse(data);
    },
  });
}
